/** In-app notifications: receipts, reminders and approvals, newest first, each opening its screen. */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { api, groupPath } from '../api/client'
import { keys, useNotifications } from '../api/hooks'
import type { Notification } from '../api/types'
import { EmptyState, ErrorState, Icon } from '../components'
import { useDateFormat } from '../i18n/useDateFormat'
import { cx } from '../lib/cx'
import { useGroup } from '../session/useSession'
import { PageLoading } from './PageLoading'

/** The SMS text ends with a call to action and a web address; in the app the row itself opens the screen. */
function inAppText(body: string): string {
  return body.replace(/\s*(?:[A-Z][\w' ]{0,24}:\s*)?https?:\/\/\S+/g, '').trim()
}

/** Links from before the signed-in screens moved under /app still open the right screen. */
function appPath(link: string): string {
  return link.startsWith('/app') ? link : `/app${link}`
}

export function NotificationsPage() {
  const { t } = useTranslation()
  const { formatMoment } = useDateFormat()
  const { groupId } = useGroup()
  const queryClient = useQueryClient()
  const list = useNotifications(groupId)
  // Opening the list counts as reading it: the bell's count clears at once, while the
  // "New" marks stay on this visit so she can still see what came in.
  const markRead = useMutation({
    mutationFn: () => api.post(groupPath(groupId, 'notifications')),
    onSuccess: () => queryClient.setQueryData<{ notifications: Notification[]; unread: number }>(
      keys.notifications(groupId), (old) => (old ? { ...old, unread: 0 } : old)),
  })
  const marked = useRef(false)
  const unread = list.data?.unread ?? 0
  useEffect(() => {
    if (unread > 0 && !marked.current) {
      marked.current = true
      markRead.mutate()
    }
  }, [unread, markRead])

  if (list.isPending) return <PageLoading />
  if (list.isError) return <ErrorState onRetry={() => void list.refetch()} />
  const items = list.data.notifications

  return (
    <div className="container screen stack">
      <title>{t('shell.notificationsPage.metaTitle')}</title>
      <h1 className="t-title">{t('shell.notificationsPage.title')}</h1>
      {items.length === 0 ? (
        <EmptyState icon="notifications" title={t('shell.notificationsPage.emptyTitle')}
          body={t('shell.notificationsPage.emptyBody')} />
      ) : (
        <ul className="notifications">
          {items.map((n) => {
            const body = (
              <>
                <span className="notifications__title">
                  {!n.read && <span className="notifications__new">{t('shell.notificationsPage.new')}</span>}
                  {n.title}
                </span>
                <span className="notifications__body">{inAppText(n.body)}</span>
                <span className="notifications__time">{formatMoment(n.created_at)}</span>
              </>
            )
            return (
              <li key={n.id} className={cx('notifications__item', !n.read && 'notifications__item--unread')}>
                {n.link.startsWith('/') ? (
                  <Link to={appPath(n.link)} className="notifications__link">
                    <span className="notifications__text">{body}</span>
                    <Icon name="chevron_right" />
                  </Link>
                ) : (
                  <div className="notifications__link"><span className="notifications__text">{body}</span></div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
